import { useEffect, useRef, useState } from "react";
import { useGetAttachmentDocumentTypeList, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { postOcrExtractTextApi, postOcrExtractTextByPathApi } from "@/api/services/main/talent/ocr-extract-text";
import { TExtendFile } from "@/types/file";
import {
  INDUSTRIES_REQUIRING_FORMAL_CERTIFICATE,
  SPECIFIC_SKILL_CERTIFICATE_LABEL,
} from "@/features/talent/constants/certificate-warning";
import {
  computeWarning,
  IDLE_STATE,
  TOcrCacheEntry,
  TWarningState,
} from "@/features/talent/hooks/certificate-notification-check-logic";

export type { TOcrCacheEntry, TWarningState };
export { computeWarning, IDLE_STATE };

/**
 * Check whether a File object and an "existing" TExtendFile are the same file reference.
 * File objects compared by identity (same upload instance). Existing files compared by id.
 */
const getFileKey = (file: TExtendFile): string | null => {
  // id is assigned by TalentDropZone for both new File and existing files; use it as cache key.
  return file?.id ?? null;
};

// MIME types the BE OCR endpoint can extract text from (IROHANA_MATCH_DEV-330).
// Keep in sync with backend talent.controller.ts fileFilter whitelist. Files outside
// this set (legacy .doc / .ppt / .xls, .zip, .rar) are skipped on the FE so we don't
// burn a round-trip getting a 400.
const OCR_SUPPORTED_NON_IMAGE_MIMES = new Set<string>([
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
]);

const isOcrSupportedFile = (file: File): boolean => {
  const mime = file.type || "";
  return mime.startsWith("image/") || OCR_SUPPORTED_NON_IMAGE_MIMES.has(mime);
};

/**
 * Hook that runs OCR on every file whose document-type dropdown is set to 「特定技能の合格書」
 * and exposes per-file warning state (loading / msg1 / msg2).
 *
 * Cache semantics (keyed by `file.id`):
 * - Each file is OCR-ed at most once, only when the target type is selected.
 * - Changing the type away from 合格書 → cache kept, warning hides. Switching back reuses cache.
 * - Removing a file → its cache entry is dropped on the next render (stale-key cleanup).
 * - Unmount / deps change → in-flight requests aborted.
 *
 * OCR endpoint routing (IROHANA_MATCH_DEV-330 follow-up):
 * - Fresh upload (`File` instance) → `POST /talents/ocr/extract-text` (multipart upload).
 * - Existing server-side file (filePath only, edit mode) → `POST /talents/:talentId/ocr/extract-text-by-path`
 *   which hits the BE in-memory OCR cache. Requires `talentId`.
 *
 * Tested behaviors match TC01..TC13 in the feature brainstorm.
 */
export const useCertificateNotificationCheck = (
  displayFiles: TExtendFile[],
  relatedFileTypes: (string | number | null | undefined)[],
  companySpecificIndustryId: number | null | undefined,
  talentId: number | null | undefined,
) => {
  const { data: attachmentDocumentTypes } = useGetAttachmentDocumentTypeList();
  const { data: companySpecificIndustries } = useGetCompanySpecificIndustryList();

  // Map<file.id, { loading, text }> — component-local state; per-file OCR cache.
  const [cache, setCache] = useState<Map<string, TOcrCacheEntry>>(new Map());
  // Abort controllers per in-flight file id so we can cancel on unmount or deps change.
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  // Set of file ids whose OCR call has resolved (success or silent-fail). Kept in a ref so
  // Effect 2 doesn't need `cache` in its dep array (which would cause extra re-runs every time
  // loading → result transitions). Combined with `abortControllersRef` this covers in-flight
  // + completed without re-triggering the effect.
  const completedIdsRef = useRef<Set<string>>(new Set());

  // Resolve target document type id from master data by label match. Runtime-resolved so a
  // seed rename doesn't silently break the feature (warn console once).
  const targetTypeId = (() => {
    const match = attachmentDocumentTypes?.find((t) => t.documentTypeName === SPECIFIC_SKILL_CERTIFICATE_LABEL);
    if (!match && attachmentDocumentTypes && attachmentDocumentTypes.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(`[cert-warning] Attachment type label not found: "${SPECIFIC_SKILL_CERTIFICATE_LABEL}". Warning disabled.`);
    }
    return match?.id;
  })();

  // Industry name lookup for msg2 gating.
  const industryName = companySpecificIndustries?.find((i) => i.id === companySpecificIndustryId)?.companySpecificIndustry;
  const isTargetIndustry = !!industryName && (INDUSTRIES_REQUIRING_FORMAL_CERTIFICATE as readonly string[]).includes(industryName);

  // Effect 1: clean stale cache entries for files that no longer exist.
  useEffect(() => {
    const currentIds = new Set(displayFiles.map((f) => getFileKey(f)).filter((id): id is string => !!id));
    setCache((prev) => {
      let changed = false;
      const next = new Map(prev);
      Array.from(prev.keys()).forEach((id) => {
        if (!currentIds.has(id)) {
          next.delete(id);
          // also abort anything still in-flight for this removed id
          abortControllersRef.current.get(id)?.abort();
          abortControllersRef.current.delete(id);
          completedIdsRef.current.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [displayFiles]);

  // Effect 2: trigger OCR for each file currently tagged as 合格書 and not yet attempted.
  // `cache` intentionally NOT in deps — completion state lives in refs to avoid extra re-runs.
  useEffect(() => {
    if (!targetTypeId) return;

    displayFiles.forEach((file, index) => {
      const fileId = getFileKey(file);
      if (!fileId) return;

      const typeValue = relatedFileTypes[index];
      const typeMatches = typeValue !== undefined && typeValue !== null && String(typeValue) === String(targetTypeId);
      if (!typeMatches) return;

      // Already completed or in flight → skip.
      if (completedIdsRef.current.has(fileId) || abortControllersRef.current.has(fileId)) return;

      // File-kind branching:
      //   - File instance (fresh upload) → multipart endpoint
      //   - filePath-only (existing server-side file in edit mode) → cached-by-path endpoint
      //   - neither → skip
      const filePath = (file as TExtendFile & { filePath?: string }).filePath;
      const isFreshUpload = file instanceof File;
      const isExistingFile = !isFreshUpload && !!filePath && !!talentId;
      if (!isFreshUpload && !isExistingFile) return;

      // MIME guard only meaningful for fresh uploads — for existing files we don't know the
      // mime client-side; the BE inspects content-type from S3 when downloading.
      if (isFreshUpload && !isOcrSupportedFile(file)) {
        completedIdsRef.current.add(fileId);
        setCache((prev) => new Map(prev).set(fileId, { loading: false, text: "" }));
        return;
      }

      const controller = new AbortController();
      abortControllersRef.current.set(fileId, controller);
      setCache((prev) => new Map(prev).set(fileId, { loading: true, text: null }));

      const ocrPromise = isFreshUpload
        ? postOcrExtractTextApi(file, controller.signal)
        : postOcrExtractTextByPathApi(talentId as number, filePath as string, controller.signal);

      ocrPromise
        .then(({ text }) => {
          // Guard against races where the file was removed mid-flight: don't resurrect a deleted key.
          if (controller.signal.aborted) return;
          completedIdsRef.current.add(fileId);
          setCache((prev) => new Map(prev).set(fileId, { loading: false, text }));
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          // Silent fail: treat as empty text. No warning rendered.
          completedIdsRef.current.add(fileId);
          setCache((prev) => new Map(prev).set(fileId, { loading: false, text: "" }));
        })
        .finally(() => {
          abortControllersRef.current.delete(fileId);
        });
    });
  }, [displayFiles, relatedFileTypes, targetTypeId, talentId]);

  // Effect 3: abort all in-flight requests on unmount.
  useEffect(() => {
    const controllers = abortControllersRef.current;
    const completed = completedIdsRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
      completed.clear();
    };
  }, []);

  const getWarning = (fileId: string | null | undefined, index: number): TWarningState => {
    if (!fileId) return IDLE_STATE;
    return computeWarning(cache.get(fileId), relatedFileTypes[index], targetTypeId, isTargetIndustry);
  };

  return { getWarning };
};
